import React, { useState } from "react";
import {
  Alert,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import {
  Header,
  type ScrollHeaderProps,
} from "@codeherence/react-native-header";
import Animated, {
  interpolate,
  useAnimatedStyle,
} from "react-native-reanimated";
import * as Sentry from "@sentry/react-native";
import { useRouter } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import BackButton from "@/Components/Headers/Components/BackButton";
import type { UserLikableImageMedia } from "@/schemas/user/image";
import UserImageLoading from "@/Components/Media/User/Images/UserImageLoading";
import UserImageModal from "@/Components/Media/User/Images/UserImageModal";
import { ImageOptionAction } from "@/Components/Media/User/Images/UserImageOptions";
import type { AppUserSession } from "@/schemas/auth/session";
import MediaService from "@/services/user/media";

type Props = {
  session: AppUserSession;
  imageMedia: UserLikableImageMedia;
  imageDisplayHeight: number;
  // onPressHeader?: () => void;
};

function ImageDetailsHeader({
  showNavBar,
  scrollY,
  session,
  imageMedia,
  imageDisplayHeight,
  // onPressHeader,
}: ScrollHeaderProps & Props): React.JSX.Element {
  const router = useRouter();

  const { width, height } = useWindowDimensions();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const [bannerHeight, setBannerHeight] = useState(48);

  const [isImageModalVisible, setIsImageModalVisible] = useState(false);

  const isReadOnly = session?.identity?.username !== imageMedia.image?.username;
  const allowedActions = [
    ImageOptionAction.CLOSE,
    isReadOnly ? ImageOptionAction.REPORT : ImageOptionAction.DELETE,
  ];

  const handleImageAction = async (
    action: ImageOptionAction,
  ): Promise<void> => {
    const mediaService = new MediaService(session);

    switch (action) {
      case ImageOptionAction.CLOSE:
        setIsImageModalVisible(false);
        break;
      case ImageOptionAction.DELETE:
        try {
          await mediaService.deleteImage(imageMedia.image.id);
          setIsImageModalVisible(false);
          router.canGoBack() && router.back();
        } catch (error) {
          Alert.alert("Error", "Failed to delete image. Please try again.", [
            { text: "OK" },
          ]);
          Sentry.captureException(error, {
            extra: {
              message:
                "Failed to delete image in ImageHeaderCard handleImageAction",
            },
          });
        }
        break;
      default:
        setIsImageModalVisible(false);
    }
  };

  const stickyOffset = safeAreaTop + 48;
  const maxTranslation = Math.max(imageDisplayHeight - stickyOffset, 0);

  const bannerTranslationStyle = useAnimatedStyle(() => {
    const bannerTranslation = interpolate(
      scrollY.value,
      [0, maxTranslation],
      [0, -maxTranslation],
      "clamp",
    );

    return { transform: [{ translateY: bannerTranslation }] };
  });

  const animatedScaleStyle = useAnimatedStyle(() => {
    const bannerHeightRatio = height / bannerHeight;
    const scaleY = interpolate(
      scrollY.value,
      [0, -(height + bannerHeight)],
      [1, bannerHeightRatio],
      "clamp",
    );

    return {
      transform: [{ scaleY }, { scaleX: scaleY }],
    };
  }, [height, bannerHeight]);

  return (
    <View style={styles.container}>
      {/* background image */}
      <Animated.View style={[StyleSheet.absoluteFill, bannerTranslationStyle]}>
        <Animated.View
          onLayout={(e) => setBannerHeight(e.nativeEvent.layout.height)}
          style={[animatedScaleStyle]}
        >
          <View style={{ marginBottom: 0 }}>
            <Animated.View style={[StyleSheet.absoluteFill]} />
            <Pressable
              onPress={() => {
                setIsImageModalVisible(true);
              }}
              style={{ zIndex: 5 }}
            >
              <UserImageLoading
                imageUrl={imageMedia.image.attributes.url}
                width={width}
                height={imageDisplayHeight}
                borderRadiusTop={0}
                borderRadiusBottom={0}
              />
            </Pressable>
          </View>
        </Animated.View>
      </Animated.View>
      {isImageModalVisible && (
        <UserImageModal
          image={imageMedia.image}
          allowedOptions={allowedActions}
          onOptionSelected={(action: ImageOptionAction) =>
            handleImageAction(action)
          }
        />
      )}

      {/* header */}
      <Header
        showNavBar={showNavBar}
        headerStyle={styles.headerStyle}
        noBottomBorder
        headerLeft={
          <BackButton
            style={styles.backButton}
            iconColor="rgba(255, 255, 255, 0.95)"
          />
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  headerStyle: {
    backgroundColor: "transparent",
  },
  backButton: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 100,
    padding: 7,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99,
  },
});

export default ImageDetailsHeader;
